import React, { useEffect } from "react";

import { SetterOrUpdater, useResetRecoilState } from "recoil";

import { Resale, ResaleFragment, ResaleStatuses, SortDirection, useListGroupedResalesQuery } from "@api/graphql";
import { GroupedResales } from "@components/grouped_resales/GroupedResales";
import { ResalesAccordionHeader } from "@components/grouped_resales/ResaleAccordionHeader";
import { ProtectedComponent, useIsAuthUserAllowed } from "@components/permission_control/ProtectedComponent";
import { BatchActionButtons } from "@components/table/BatchActionButtons";
import { ResaleStatusComponentType } from "@components/table/ProvisionalResaleTable";
import { TableHeader } from "@components/table/TableContents";
import { useAuthenticationContext } from "@context/authentication";
import { ExternalRoles, Role } from "@roles";
import { SelectedResalesType, selectedResales } from "@states/resale_submission";
import { FilterByTyped, SortByTyped } from "@util/index";
import { ResaleGroup } from "@util/resale";

import styles from "./ReportsToReview.module.scss";

const useTableHeaders = (): TableHeader<ResaleFragment>[] => {
  const isVaAdmin = useIsAuthUserAllowed([Role.VisualArtistAdmin]);
  const headers: TableHeader<ResaleFragment>[] = [
    { label: "ID", accessor: "resale_id" },
    { label: "Artwork title", accessor: "artwork_name" },
    { label: "Artist", accessor: "artist_name" },
    { label: "Sold date", accessor: "date_sold" },
    { label: "Resale price", accessor: "resale_price" },
    { label: "Royalty", accessor: "royalty" },
  ];
  // Permission: only VA admin able to show Resale Status dropdown.
  if (isVaAdmin) {
    headers.push({
      label: "Resale status",
      accessor: "resale_status",
      componentType: ResaleStatusComponentType.DROPDOWN,
    });
  }

  return headers;
};

const getTableToolbar = ({ batch_grouping_id, isProvisionalEligibleExist }: ResaleGroup, refetch?: () => void) => (
  <BatchActionButtons
    batchGroupingId={batch_grouping_id}
    isProvisionalEligibleExist={isProvisionalEligibleExist}
    displayBatchUpdateResaleStatusAndArtistButton
    refetch={refetch}
  />
);

export const ReportsToReview = () => {
  const { auth } = useAuthenticationContext();
  const resetSelectedResales = useResetRecoilState(selectedResales);
  const isExternalUser = useIsAuthUserAllowed(ExternalRoles);
  useEffect(() => {
    resetSelectedResales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // leave it blank so the selected resales got reset only once the page is mounted

  const filter_by: FilterByTyped<Resale>[] = [
    {
      field: "batch_group_confirmed",
      eq: "false",
    },
  ];

  const sort_by: SortByTyped<Resale> = {
    field: "reported_on",
    direction: SortDirection.DESCENDING,
  };

  const resaleStatusFilterOptions = [
    {
      option: ResaleStatuses.CONFIRMED_ELIGIBLE,
      defaultValue: true,
    },
    {
      option: ResaleStatuses.PROVISIONALLY_ELIGIBLE,
      defaultValue: true,
    },
    {
      option: ResaleStatuses.CONFIRMED_INELIGIBLE,
      defaultValue: false,
    },
    {
      option: ResaleStatuses.PROVISIONALLY_INELIGIBLE,
      defaultValue: false,
    },
    {
      option: ResaleStatuses.RESEARCH_REQUIRED,
      defaultValue: false,
    },
    {
      option: ResaleStatuses.OPTED_OUT,
      defaultValue: false,
    },
    {
      option: ResaleStatuses.INVALID,
      defaultValue: false,
    },
  ];

  const handleHeaderCheckBoxChange =
    (setRecoilSelectedRows: SetterOrUpdater<SelectedResalesType>, resales: ResaleFragment[]) =>
    (e: React.ChangeEvent<HTMLInputElement>, batchID: string) => {
      if (e.target.checked) {
        setRecoilSelectedRows((prevSelectedRows) => {
          return {
            ...prevSelectedRows,
            [batchID]: resales.map((resale) => ({
              resaleId: resale.resale_id,
              invoice:
                resale.invoice && resale.invoice_line
                  ? {
                      invoice: resale.invoice,
                      invoice_line: resale.invoice_line,
                    }
                  : undefined,
              artistId: resale.artist_id,
            })),
          };
        });
      } else {
        setRecoilSelectedRows((prevSelectedRows) => ({
          ...prevSelectedRows,
          [batchID]: [],
        }));
      }
    };
  const getAccordionHeader = ({ resales }: ResaleGroup) => {
    // Permission: only VA, MS, Data able to view reporter name and sum of royalty payable, company name
    return (
      <ProtectedComponent requiredGroups={[Role.VisualArtistAdmin, Role.MemberServices, Role.Data]} auth={auth}>
        <ResalesAccordionHeader resales={resales} isExternalUser={isExternalUser} resaleCount={resales.length} />
      </ProtectedComponent>
    );
  };
  return (
    <GroupedResales
      queryFunction={useListGroupedResalesQuery}
      getQueryVariables={(page) => ({
        input: { page, limit: 10, filter_by, sort_by },
      })}
      updateQueryVariablesWithFilter={({ input: { page, limit, filter_by, sort_by } }, new_filter_by) => ({
        input: {
          page,
          limit,
          filter_by: filter_by.concat(new_filter_by ? new_filter_by : []),
          sort_by,
        },
      })}
      getResaleGroupsFromQueryData={(data) => data.listGroupedResales.results}
      getPaginatedOutputFromQueryData={(data) => data.listGroupedResales}
      getAccordionHeader={getAccordionHeader}
      customTableHeaders={useTableHeaders()}
      getTableToolbar={getTableToolbar}
      sourcePage={"ReportsToReview"}
      resaleStatusFilterOptions={resaleStatusFilterOptions}
      customTableStyleClassName={styles["customized-column-width"]}
      showCheckBox
      handleHeaderCheckBoxChange={handleHeaderCheckBoxChange}
    />
  );
};
